import { useState, useEffect, useRef, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { fetchApiRaw, GET_ISSUERS } from 'r/utils/fetchApiRaw';
import type { IssuerWithPerformanceMetrics } from 'r/types';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const PAGE_SIZE = 50;

interface AuthorPage {
  data: IssuerWithPerformanceMetrics[];
  total: number;
}

interface AuthorFilterDropdownProps {
  filterMode: 'all' | 'kols';
  excludedAuthors: string[]; // twitterIDs
  onFilterModeChange: (mode: 'all' | 'kols') => void;
  onExcludedAuthorsChange: (excluded: string[]) => void;
  onReset: () => void;
  isDisabled?: boolean;
}

export function AuthorFilterDropdown({
  filterMode,
  excludedAuthors,
  onFilterModeChange,
  onExcludedAuthorsChange,
  onReset,
  isDisabled,
}: AuthorFilterDropdownProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const buildFilter = (mode: 'all' | 'kols', q: string): string | undefined => {
    const parts: string[] = [];
    if (mode === 'kols') parts.push('"adminInfo.isKol":true');
    if (q.trim()) {
      const safe = q.trim().replace(/"/g, '\\"');
      parts.push(`"username":{"$ilike":"%${safe}%"}`);
    }
    return parts.length > 0 ? parts.join(',') : undefined;
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery<AuthorPage>({
      queryKey: ['author-filter-dropdown', filterMode, debouncedSearch],
      queryFn: async ({ pageParam }) => {
        const filter = buildFilter(filterMode, debouncedSearch);
        const queryParams: Record<string, string> = {
          skip: String((pageParam as number) * PAGE_SIZE),
          take: String(PAGE_SIZE),
          sort: 'username:asc',
        };
        if (filter) queryParams.filter = filter;
        const response = await fetchApiRaw(GET_ISSUERS, { queryParams });
        return {
          data:
            Array.isArray(response.data) ?
              (response.data as IssuerWithPerformanceMetrics[]).filter(
                (a) => a.username != null,
              )
            : [],
          total: response.total || 0,
        };
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
        return loaded < lastPage.total ? allPages.length : undefined;
      },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    });

  const authors = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data],
  );

  // Load more when sentinel becomes visible
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetchingNextPage) void fetchNextPage();
      },
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const isActive = filterMode !== 'all' || excludedAuthors.length > 0;

  const triggerLabel = useMemo(() => {
    if (filterMode === 'kols' && excludedAuthors.length === 0)
      return 'KOLs only';
    if (filterMode === 'kols') return `KOLs only (−${excludedAuthors.length})`;
    if (excludedAuthors.length > 0)
      return `Excluding ${excludedAuthors.length}`;
    return 'All Authors';
  }, [filterMode, excludedAuthors]);

  const toggleExclude = (twitterID: string | null, include: boolean) => {
    if (!twitterID) return;
    if (include) {
      onExcludedAuthorsChange(excludedAuthors.filter((id) => id !== twitterID));
    } else if (!excludedAuthors.includes(twitterID)) {
      onExcludedAuthorsChange([...excludedAuthors, twitterID]);
    }
  };

  const handleModeChange = (mode: 'all' | 'kols') => {
    setSearch('');
    setDebouncedSearch('');
    onFilterModeChange(mode);
  };

  const handleReset = () => {
    setSearch('');
    setDebouncedSearch('');
    onReset();
  };

  return (
    <div className="flex flex-col gap-1">
      <Label className="text-sm font-medium">Authors</Label>
      <div className="relative w-80">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={isActive ? 'default' : 'outline'}
              className={`w-full justify-between font-normal ${isActive ? 'pr-8' : ''}`}
              disabled={isDisabled}
            >
              <span className="truncate">{triggerLabel}</span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 opacity-60 ${isActive ? 'hidden' : ''}`}
              />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-80 p-0" align="start">
            {/* Mode options */}
            <div className="flex flex-col gap-0.5 p-1.5">
              <ModeOption
                label="All"
                isSelected={filterMode === 'all'}
                onClick={() => handleModeChange('all')}
              />
              <ModeOption
                label="Show KOLs only"
                isSelected={filterMode === 'kols'}
                onClick={() => handleModeChange('kols')}
              />
            </div>

            <Separator />

            {/* Search */}
            <div className="p-2">
              <Input
                placeholder={
                  filterMode === 'all' ?
                    'Search to exclude...'
                  : 'Search KOLs...'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            {/* Author list */}
            <div className="flex max-h-52 flex-col overflow-y-auto px-2 pb-1">
              {isFetching && authors.length === 0 ?
                <div className="flex justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              : authors.length === 0 ?
                <p className="py-3 text-center text-sm text-muted-foreground">
                  No authors found
                </p>
              : <>
                  {authors.map((author) => {
                    const canExclude = !!author.twitterID;
                    const isIncluded =
                      !canExclude ||
                      !excludedAuthors.includes(author.twitterID!);
                    return (
                      <label
                        key={author.id}
                        className={`flex select-none items-center gap-2 rounded px-1 py-1.5 hover:bg-accent ${canExclude ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                      >
                        <Checkbox
                          checked={isIncluded}
                          disabled={!canExclude}
                          onCheckedChange={(checked) =>
                            toggleExclude(author.twitterID, checked as boolean)
                          }
                        />
                        <span className="flex-1 truncate text-sm">
                          {author.username}
                        </span>
                        {author.predictionsCount > 1 && (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {author.predictionsCount}
                          </span>
                        )}
                      </label>
                    );
                  })}
                  {/* Infinite scroll sentinel */}
                  <div ref={sentinelRef} className="h-px" />
                  {isFetchingNextPage && (
                    <div className="flex justify-center py-2">
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </>
              }
            </div>

            {isActive && (
              <>
                <Separator />
                <div className="p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-full text-sm text-muted-foreground hover:text-foreground"
                    onClick={handleReset}
                  >
                    Clear filter
                  </Button>
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>

        {isActive && (
          <button
            type="button"
            className="absolute right-2 top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-primary-foreground/70 hover:text-primary-foreground"
            onClick={handleReset}
            disabled={isDisabled}
            aria-label="Clear author filter"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function ModeOption({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
      onClick={onClick}
    >
      <span
        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
          isSelected ? 'border-primary' : 'border-muted-foreground/40'
        }`}
      >
        {isSelected && (
          <span className="block h-1.5 w-1.5 rounded-full bg-primary" />
        )}
      </span>
      <span className={isSelected ? 'font-medium' : ''}>{label}</span>
    </button>
  );
}
